import React, { useState } from "react";
import Select, { createFilter } from "react-select";
import VirtualizedList from "../../../common/virtualList";
import { DelDatePicker } from "../../../common/DelDatePicker";
// Client Signatures moved to Misc tab - import removed
import styles from "./ProjectComponents.module.css";
import { delSelect } from "../../../common/delSelect";
import { useAppStore } from "../../../../store/appStore";
import { PROJECT_DESCRIPTION_MAX_LENGTH } from "../../../../constants/delConstants";

// Compute touch-aware React Select props to avoid iOS virtual keyboard detaching menus
const useTouchAwareSelectProps = () => {
  const isTouch = useAppStore((s) => s.landingPage?.isTouch);
  const portalTarget =
    typeof document !== "undefined" ? document.body : undefined;
  if (isTouch) {
    return {
      menuPosition: "absolute",
      menuPlacement: "auto",
      menuShouldScrollIntoView: false,
      menuPortalTarget: portalTarget,
      styles: {
        menu: (base) => ({ ...base, zIndex: 9999 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      },
    };
  }
  return {
    menuPosition: "fixed",
    menuPortalTarget: portalTarget,
    styles: {
      menu: (base) => ({ ...base, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    },
  };
};

// Project selection input. This will be used to select a project and the associated information with it.
export const ProjectSelectionInput = ({ callbacks }) => {
  const touchSelectProps = useTouchAwareSelectProps();
  // Setting up a menu list component with the virtualized list component
  const MenuList = ({ children, maxHeight, dataTestId }) => {
    const itemCount = Array.isArray(children) ? children.length : 1;
    const itemHeight = 42; // 8px top padding + 8px bottom padding + 14px font + line height
    const listHeight = Math.min(maxHeight, itemCount * itemHeight);
    return (
      <VirtualizedList
        items={Array.isArray(children) ? children : [children]}
        height={listHeight}
        width="100%"
        itemHeight={itemHeight}
        renderItem={(child) => child}
        className={dataTestId}
      />
    );
  };

  const handleProjectSelect = (selected) => {
    callbacks.setSelectedProject(
      selected,
      callbacks.allEmployees,
      callbacks.emitPatch,
    );
  };

  return (
    <>
      <div className={styles.selectContainer}>
        <div className={styles.projectInfoLabelContainer}>
          <label className={styles.inputLabel}>Project</label>
          <button
            type="button"
            onClick={() => callbacks.clearAllProjectInfo(callbacks.emitPatch)}
            className={styles.clearProjectInfoButton}
            disabled={callbacks.delMode === "view"}
          >
            Clear Project Info
          </button>
        </div>
        {callbacks.delData.projectInfo.inactiveProjectInfo && (
          <div className={styles.inactiveProjectInfoContainer}>
            <p className={styles.inactiveProjectInfoText}>
              This project is inactive in Deltek.
            </p>
          </div>
        )}

        <div>
          <Select
            classNamePrefix="project-search-input"
            value={
              callbacks.delData.projectInfo.projectNumber
                ? {
                    value: callbacks.delData.projectInfo.projectNumber,
                    label: callbacks.delData.projectInfo.projectLabel,
                    client_name: callbacks.delData.projectInfo.clientName,
                    phase_list: callbacks.delData.projectInfo.availablePhases,
                    invoice_manager:
                      callbacks.delData.projectInfo.invoiceManager,
                    project_manager:
                      callbacks.delData.projectInfo.projectManager,
                  }
                : "Select Project"
            }
            onChange={handleProjectSelect}
            options={callbacks.allProjectInfo}
            isSearchable={true}
            isClearable={true}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            placeholder={
              callbacks.projectErrors?.projectNumber ? (
                <span className={styles.errorText}>
                  {callbacks.projectErrors.projectNumber}
                </span>
              ) : (
                "Search projects..."
              )
            }
            styles={{
              ...delSelect,
              ...(touchSelectProps.styles || {}),
            }}
            menuPortalTarget={touchSelectProps.menuPortalTarget}
            menuPosition={touchSelectProps.menuPosition}
            menuPlacement={touchSelectProps.menuPlacement}
            menuShouldScrollIntoView={touchSelectProps.menuShouldScrollIntoView}
            isDisabled={callbacks.delMode === "view"}
            filterOptions={createFilter({ ignoreAccents: false })}
            components={{
              MenuList: (props) => (
                <MenuList {...props} dataTestId="project-search-result" />
              ),
            }}
            className={
              callbacks.projectErrors?.projectNumber ||
              callbacks.getAutosaveFieldError?.("project_number")
                ? styles.errorSelectContainer
                : ""
            }
          />
          {callbacks.getAutosaveFieldError?.("project_number") && (
            <span className={styles.fieldErrorText}>
              {callbacks.getAutosaveFieldError("project_number")}
            </span>
          )}
        </div>
      </div>
    </>
  );
};

// Creating the phase input
export const PhaseSelectionInput = ({ callbacks }) => {
  const touchSelectProps = useTouchAwareSelectProps();
  const PhaseMenuList = ({ children, maxHeight, dataTestId }) => {
    const itemCount = Array.isArray(children) ? children.length : 1;
    const itemHeight = 42; // 8px top padding + 8px bottom padding + 14px font + line height
    const listHeight = Math.min(maxHeight, itemCount * itemHeight);
    return (
      <VirtualizedList
        items={Array.isArray(children) ? children : [children]}
        height={listHeight}
        width="100%"
        itemHeight={itemHeight}
        renderItem={(child) => child}
        className={dataTestId}
      />
    );
  };

  const handlePhaseSelect = (selected) => {
    callbacks.setSelectedPhase(selected, callbacks.emitPatch);
  };

  return (
    <>
      <div className={styles.selectContainer}>
        <label className={styles.inputLabel}>Phase</label>
        <Select
          className={
            callbacks.projectErrors?.phaseId ||
            callbacks.getAutosaveFieldError?.("phase_id")
              ? styles.errorSelectContainer
              : ""
          }
          value={
            callbacks.delData.projectInfo.phaseId
              ? {
                  value: callbacks.delData.projectInfo.phaseId,
                  label: callbacks.delData.projectInfo.phaseName,
                }
              : "Select Phase"
          }
          onChange={handlePhaseSelect}
          isDisabled={
            !callbacks.delData.projectInfo.projectNumber ||
            callbacks.delMode === "view"
          }
          options={callbacks.delData.projectInfo.availablePhases}
          placeholder={
            callbacks.projectErrors?.phaseId ? (
              <span className={styles.errorText}>
                {callbacks.projectErrors.phaseId}
              </span>
            ) : !callbacks.delData.projectInfo.projectNumber ? (
              "Select a project first"
            ) : callbacks.delData.projectInfo.availablePhases.length ? (
              "Select Phase"
            ) : (
              "No Phases For This Project"
            )
          }
          noOptionsMessage={() =>
            callbacks.delData.projectInfo.projectNumber &&
            callbacks.delData.projectInfo.availablePhases.length
              ? "No phases available"
              : "Select a project first"
          }
          styles={{
            ...delSelect,
            ...(touchSelectProps.styles || {}),
          }}
          menuPortalTarget={touchSelectProps.menuPortalTarget}
          menuPosition={touchSelectProps.menuPosition}
          menuPlacement={touchSelectProps.menuPlacement}
          menuShouldScrollIntoView={touchSelectProps.menuShouldScrollIntoView}
          isClearable={true}
          isSearchable={true}
          getOptionValue={(option) => option.value}
          getOptionLabel={(option) => option.label}
          components={{
            MenuList: (props) => (
              <PhaseMenuList {...props} dataTestId="phase-search-result" />
            ),
          }}
        />
        {callbacks.getAutosaveFieldError?.("phase_id") && (
          <span className={styles.fieldErrorText}>
            {callbacks.getAutosaveFieldError("phase_id")}
          </span>
        )}
      </div>
    </>
  );
};

export const TaskSelectionInput = ({ callbacks }) => {
  const touchSelectProps = useTouchAwareSelectProps();
  //const { delData, errors, delMode, selectedTask, availableTasks } = callbacks;
  const TaskMenuList = ({ children, maxHeight, dataTestId }) => {
    const itemCount = Array.isArray(children) ? children.length : 1;
    const itemHeight = 42; // 8px top padding + 8px bottom padding + 14px font + line height
    const listHeight = Math.min(maxHeight, itemCount * itemHeight);
    return (
      <VirtualizedList
        items={Array.isArray(children) ? children : [children]}
        height={listHeight}
        width="100%"
        itemHeight={itemHeight}
        renderItem={(child) => child}
        className={dataTestId}
      />
    );
  };

  const handleTaskSelect = (selected) => {
    callbacks.setSelectedTask(selected, callbacks.emitPatch);
  };

  return (
    <>
      <div className={styles.selectContainer}>
        <label className={styles.inputLabel}>Task</label>
        <Select
          className={
            callbacks.projectErrors?.taskId ||
            callbacks.getAutosaveFieldError?.("task_id")
              ? styles.errorSelectContainer
              : ""
          }
          value={
            callbacks.delData.projectInfo.taskId
              ? {
                  value: callbacks.delData.projectInfo.taskId,
                  label: callbacks.delData.projectInfo.taskName,
                }
              : "Select Task"
          }
          onChange={handleTaskSelect}
          isDisabled={
            !callbacks.delData.projectInfo.phaseId ||
            callbacks.delMode === "view" ||
            !callbacks.delData.projectInfo.availableTasks.length
          }
          options={callbacks.delData.projectInfo.availableTasks}
          placeholder={
            callbacks.projectErrors?.taskId ? (
              <span className={styles.errorText}>
                {callbacks.projectErrors.taskId}
              </span>
            ) : !callbacks.delData.projectInfo.phaseId ? (
              "Select a phase first"
            ) : callbacks.delData.projectInfo.availableTasks.length ? (
              "Select Task"
            ) : (
              "No Tasks For This Phase"
            )
          }
          noOptionsMessage={() =>
            callbacks.delData.projectInfo.phaseId &&
            !callbacks.delData.projectInfo.availableTasks.length
              ? "No tasks available"
              : "Select a phase first"
          }
          styles={{
            ...delSelect,
            ...(touchSelectProps.styles || {}),
          }}
          menuPortalTarget={touchSelectProps.menuPortalTarget}
          menuPosition={touchSelectProps.menuPosition}
          menuPlacement={touchSelectProps.menuPlacement}
          menuShouldScrollIntoView={touchSelectProps.menuShouldScrollIntoView}
          isClearable={true}
          isSearchable={true}
          getOptionValue={(option) => option.value}
          getOptionLabel={(option) => option.label}
          components={{
            MenuList: (props) => (
              <TaskMenuList {...props} dataTestId="task-search-result" />
            ),
          }}
        />
        {callbacks.getAutosaveFieldError?.("task_id") && (
          <span className={styles.fieldErrorText}>
            {callbacks.getAutosaveFieldError("task_id")}
          </span>
        )}
      </div>
    </>
  );
};

// Creating the invoice manager input so we can keep it the same input reqgardless of whether there is a project number or project description
export const InvoiceManagerInput = ({ callbacks }) => {
  const touchSelectProps = useTouchAwareSelectProps();
  const handleInvoiceManagerSelect = (selected) => {
    callbacks.setSelectedInvoiceManager(selected, callbacks.emitPatch);
  };

  return (
    <>
      <div className={styles.selectContainer}>
        <label className={styles.inputInvoiceManagerLabel}>
          Invoice Manager
        </label>
        <Select
          classNamePrefix="invoice-manager-input"
          value={
            callbacks.delData.projectInfo.invoiceManager.id
              ? {
                  value: callbacks.delData.projectInfo.invoiceManager.id,
                  label: callbacks.delData.projectInfo.invoiceManager.name,
                }
              : "Select Invoice Manager"
          }
          onChange={handleInvoiceManagerSelect}
          options={callbacks.allEmployees.map((employee) => ({
            value: employee.employee_id,
            label: employee.name,
          }))}
          placeholder={
            callbacks.projectErrors?.invoiceManager ? (
              <span className={styles.errorText}>
                {callbacks.projectErrors.invoiceManager}
              </span>
            ) : (
              "Invoice Manager"
            )
          }
          isClearable={true}
          isSearchable={true}
          getOptionValue={(option) => option.value}
          getOptionLabel={(option) => option.label}
          isDisabled={callbacks.delMode === "view"}
          styles={{
            ...delSelect,
            ...(touchSelectProps.styles || {}),
          }}
          // Preserve explicit top placement, but still adjust menu positioning/portal for touch
          menuPlacement="top"
          menuPortalTarget={touchSelectProps.menuPortalTarget}
          menuPosition={touchSelectProps.menuPosition}
          menuShouldScrollIntoView={touchSelectProps.menuShouldScrollIntoView}
          className={
            callbacks.projectErrors?.invoiceManager ||
            callbacks.getAutosaveFieldError?.("invoice_manager_id")
              ? styles.errorSelectContainer
              : ""
          }
        />
        {callbacks.getAutosaveFieldError?.("invoice_manager_id") && (
          <span className={styles.fieldErrorText}>
            {callbacks.getAutosaveFieldError("invoice_manager_id")}
          </span>
        )}
      </div>
    </>
  );
};

// Project description input
export const ProjectDescriptionInput = ({ callbacks }) => {
  const handleProjectDescriptionChange = (e) => {
    callbacks.projectDescriptionChange(e.target.value, null);
  };

  const handleProjectDescriptionBlur = (e) => {
    callbacks.projectDescriptionChange(e.target.value, callbacks.emitPatch);
  };

  const handleClearProjectDescription = () => {
    callbacks.projectDescriptionChange("", callbacks.emitPatch);
  };

  return (
    <>
      <div className={styles.selectContainer}>
        <div className={styles.projectDescriptionLabelContainer}>
          <label className={styles.inputLabel}>
            Project Description
            <span className={styles.charCount}>
              {` (${(callbacks.delData.projectInfo.projectDescription || "").length}/${PROJECT_DESCRIPTION_MAX_LENGTH})`}
            </span>
          </label>
          <button
            type="button"
            onClick={handleClearProjectDescription}
            className={styles.clearProjectDescriptionButton}
            disabled={callbacks.delMode === "view"}
          >
            Clear Description
          </button>
        </div>
        <textarea
          placeholder={
            callbacks.projectErrors?.projectDescription
              ? callbacks.projectErrors.projectDescription
              : "Enter project description..."
          }
          value={callbacks.delData.projectInfo.projectDescription}
          onChange={handleProjectDescriptionChange}
          onBlur={handleProjectDescriptionBlur}
          disabled={callbacks.delMode === "view"}
          maxLength={PROJECT_DESCRIPTION_MAX_LENGTH}
          className={`${styles.projectDescriptionInput} ${
            callbacks.projectErrors?.projectDescription ||
            callbacks.getAutosaveFieldError?.("project_description")
              ? styles.inputError
              : ""
          }`}
        />
        {callbacks.getAutosaveFieldError?.("project_description") && (
          <span className={styles.fieldErrorText}>
            {callbacks.getAutosaveFieldError("project_description")}
          </span>
        )}
      </div>
    </>
  );
};

// Project date input
export const ProjectDateInput = ({ callbacks }) => {
  const handleProjectDateChange = (date) => {
    callbacks.setProjectDate(date, callbacks.emitPatch);
  };

  const hasDateError = callbacks.getAutosaveFieldError?.("del_date");

  return (
    <>
      <div className={styles.selectContainer}>
        <label className={styles.inputLabel}>Date</label>
        <DelDatePicker
          selected={callbacks.delData.projectInfo.date}
          onChange={handleProjectDateChange}
          wrapperClassName={styles.datePickerWrapper}
          className={`${styles.dateInput} ${hasDateError ? styles.inputError : ""}`}
          dateFormat="MM/dd/yyyy"
          placeholderText="Select a date"
          isClearable={true}
          isDisabled={callbacks.delMode === "view"}
          showPopperArrow={false}
          withPortal
          portalId="date-picker-portal"
          calendarClassName={styles.calendar}
        />
        {hasDateError && (
          <span className={styles.fieldErrorText}>
            {callbacks.getAutosaveFieldError("del_date")}
          </span>
        )}
      </div>
    </>
  );
};

// JSA not required checkbox - inline with No Project Number checkbox
const JsaNotRequiredCheckbox = ({ projectState }) => {
  const handleJsaNotRequiredChange = (e) => {
    projectState.setShowJsaTab(e.target.checked, projectState.emitPatch);
  };

  return (
    <label className={styles.checkboxLabel}>
      <input
        type="checkbox"
        className={styles.checkboxInput}
        checked={projectState.delData.projectInfo.jsaNotRequired}
        onChange={handleJsaNotRequiredChange}
        disabled={projectState.delMode === "view"}
      />
      JSA Not Required
    </label>
  );
};

// If the delMode is new, we will show the below layout in the switch statement
const NewDelLayout = ({ projectState }) => {
  const handleProjectNumberCheckboxChange = (e) => {
    projectState.toggleNoProjectNumberCheckbox(e.target.checked, projectState.emitPatch);
  };

  return (
    <>
      <div className={styles.projectNumberCheckboxContainer}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            className={styles.checkboxInput}
            onChange={handleProjectNumberCheckboxChange}
            disabled={projectState.delMode === "view"}
            checked={projectState.delData.projectInfo.noProjectNumberChecked}
          />
          No Project Number
        </label>
        <JsaNotRequiredCheckbox projectState={projectState} />
      </div>
      {projectState.delData.projectInfo.noProjectNumberChecked && (
        <div className={styles.projectDescriptionContainer}>
          <ProjectDescriptionInput callbacks={projectState} />
        </div>
      )}
      <div className={styles.defaultProjectInfoContainer}>
        <ProjectSelectionInput callbacks={projectState} />
        <PhaseSelectionInput callbacks={projectState} />
        <TaskSelectionInput callbacks={projectState} />
        <div className={styles.selectContainer}>
          <label className={styles.inputLabel}>Project Manager</label>
          <input
            type="text"
            value={projectState.delData.projectInfo.projectManager.name}
            className={styles.managerInput}
            disabled={true}
            data-testid="project-manager-input"
            placeholder="Project Manager"
          />
        </div>
        <InvoiceManagerInput callbacks={projectState} />
        <ProjectDateInput callbacks={projectState} />
      </div>
    </>
  );
};

// If the delMode is edit, we will show the below layout in the switch statement
const EditViewDelLayout = ({ projectState }) => {
  return (
    <>
      <div className={styles.projectNumberCheckboxContainer}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            className={styles.checkboxInput}
            onChange={(e) =>
              projectState.toggleNoProjectNumberCheckbox(e.target.checked, projectState.emitPatch)
            }
            disabled={projectState.delMode === "view"}
            checked={projectState.delData.projectInfo.noProjectNumberChecked}
          />
          No Project Number
        </label>
        <JsaNotRequiredCheckbox projectState={projectState} />
      </div>
      {projectState.delData.projectInfo.noProjectNumberChecked && (
        <div className={styles.projectDescriptionContainer}>
          <ProjectDescriptionInput callbacks={projectState} />
        </div>
      )}

      <div className={styles.defaultProjectInfoContainer}>
        <ProjectSelectionInput callbacks={projectState} />
        <PhaseSelectionInput callbacks={projectState} />
        <TaskSelectionInput callbacks={projectState} />
        <div className={styles.selectContainer}>
          <label className={styles.inputLabel}>Project Manager</label>
          <input
            type="text"
            value={projectState.delData.projectInfo.projectManager.name}
            className={styles.managerInput}
            disabled={true}
            data-testid="project-manager-input"
            placeholder="Project Manager"
          />
        </div>
        <InvoiceManagerInput callbacks={projectState} />
        <ProjectDateInput callbacks={projectState} />
      </div>
    </>
  );
};

// If the delMode is edit, we will show the below layout in the switch statement
const ApproveDelLayout = ({ projectState }) => {
  const [keepProjectDescription, setKeepProjectDescription] = useState(
    projectState.delData.projectInfo.projectDescription,
  );
  return (
    <>
      <div className={styles.projectNumberCheckboxContainer}>
        <JsaNotRequiredCheckbox projectState={projectState} />
      </div>
      {keepProjectDescription && (
        <div className={styles.projectDescriptionContainer}>
          <ProjectDescriptionInput callbacks={projectState} />
        </div>
      )}

      <div className={styles.defaultProjectInfoContainer}>
        <ProjectSelectionInput callbacks={projectState} />
        <PhaseSelectionInput callbacks={projectState} />
        <TaskSelectionInput callbacks={projectState} />
        <div className={styles.selectContainer}>
          <label className={styles.inputLabel}>Project Manager</label>
          <input
            type="text"
            value={projectState.delData.projectInfo.projectManager.name}
            className={styles.managerInput}
            disabled={true}
            data-testid="project-manager-input"
            placeholder="Project Manager"
          />
        </div>
        <InvoiceManagerInput callbacks={projectState} />
        <ProjectDateInput callbacks={projectState} />
      </div>
    </>
  );
};

// Helper to get autosave field error for a specific field
const getAutosaveFieldError = (autosaveErrors, fieldName) => {
  if (!autosaveErrors?.fieldErrors) return null;
  return autosaveErrors.fieldErrors[fieldName] || null;
};

// Project tab component, this will be the main component for the project tab, holding all the components and state for the project tab.
export const ProjectTab = ({
  projectProps,
  projectActions,
  clientSignaturesActions,
}) => {
  // Get the appropriate state slice based on tab context
  // Extract tab type from props or use a default
  const tabName = projectProps?.tabName || "drafts"; // fallback to drafts if

  // Create combined state object for components (maintains compatibility from former setup)
  const projectState = {
    // Core data
    delData: projectProps.delData,
    delMode: projectProps.delMode,
    delErrors: projectProps.delErrors,
    allProjectInfo: projectProps.allProjectInfo,
    allEmployees: projectProps.allEmployees,

    // UI state projectProps
    loading: projectProps.projectUI.loading,
    showJsaTab: projectProps.projectUI.showJsaTab,
    showWasteDisposalTab: projectProps.projectUI.showWasteDisposalTab,

    // UI State Client Signatures
    clientSignaturesUI: projectProps.clientSignaturesUI,

    // Action methods - delegate to global actions
    ...projectActions,
    ...clientSignaturesActions,

    // DEL autosave websockets - passed down from DelForm
    emitPatch: projectProps.emitPatch,

    // Autosave field errors for inline error display
    autosaveErrors: projectProps.autosaveErrors,
    getAutosaveFieldError: (fieldName) =>
      getAutosaveFieldError(projectProps.autosaveErrors, fieldName),
  };

  return (
    <div className={styles.projectContainer}>
      {projectState.loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingSpinner}></div>
        </div>
      )}

      {/* Switch statement to handle the different delModes */}
      {(() => {
        switch (projectState.delMode) {
          case "new":
            return <NewDelLayout projectState={projectState} />;
          case "copy":
            return <EditViewDelLayout projectState={projectState} />;
          case "edit":
            return <EditViewDelLayout projectState={projectState} />;
          case "view":
            return <EditViewDelLayout projectState={projectState} />;
          case "approve":
            return <ApproveDelLayout projectState={projectState} />;
          default:
            return <EditViewDelLayout projectState={projectState} />;
        }
      })()}
    </div>
  );
};
